import {
  ChangeEvent,
  HTMLAttributes,
  useEffect,
  useMemo,
  useRef,
  useState,
  CSSProperties,
} from 'react';

import { styled } from '@mui/material';
import clsx from 'clsx';

import { focusProps } from '../../../theme/utils';
import { Sizes, SxProps, WithRef } from '../../../types';
import { tokens } from '../../../theme/generated/tokens';
import { useControlLabelSettings } from '../ControlLabelContext';

const innerPadding = '2px';
const thumbSize = 'calc(1em - 4px)';
const activeCss = {
  height: 'calc(1em - 6px)',
  top: '3px',
};
const trackWidth = '1.5625em';
const duration = '.1s';

export type SwitchSize = Sizes<'sm' | 'md'>;

const defaultSize: SwitchSize = 'sm';
const styles: Record<
  SwitchSize,
  { activeCss: CSSProperties; fontSize: number }
> = {
  sm: {
    activeCss,
    fontSize: 16,
  },
  md: {
    activeCss,
    fontSize: 20,
  },
};

const Root = styled('span', { target: 'DS-Switch-Root' })({
  width: trackWidth,
  height: '1em',
  display: 'inline-block',
  borderRadius: '1em',
  position: 'relative',
  backgroundColor: tokens.theme.bg.structural.surfaceStrong,
  flexShrink: 0,
  flexGrow: 0,
});

const StyledInput = styled('input', { target: 'DS-Switch-input' })({
  position: 'absolute',
  inset: 0,
  margin: 0,
  borderRadius: 'inherit',
  opacity: 0,
  // Make it work in Safari
  width: '100%',
  height: '100%',
});

const StyledThumb = styled('span', { target: 'DS-Switch-thumb' })({
  pointerEvents: 'none',
  display: 'inline-block',
  position: 'absolute',
  width: thumbSize,
  height: thumbSize,
  left: innerPadding,
  top: innerPadding,
  borderRadius: tokens.theme.radius.full,
  transition: `height ${duration}, top ${duration}, left ${duration} ease ${duration}, transform ${duration}`,
  '.DS-Switch-input:checked + &': {
    left: `calc(100% - (${thumbSize} + ${innerPadding}))`,
  },
  '.DS-Switch-input:focus + &': focusProps(),
});

export type SwitchProps = {
  size?: SwitchSize;
  checked?: boolean;
  onChange?: (event: ChangeEvent<HTMLInputElement>, checked: boolean) => void;
  disabled?: boolean;
  sx?: SxProps;
  name?: string;
  testId?: string;
  inputId?: string;
} & Omit<HTMLAttributes<HTMLSpanElement>, 'onChange'>;

export const Switch = ({
  ref,
  size,
  name,
  checked,
  onChange,
  disabled,
  sx,
  testId,
  inputId,
  ...rest
}: WithRef<HTMLSpanElement, SwitchProps>) => {
  const cl = useControlLabelSettings();
  const thumbRef = useRef<HTMLSpanElement | null>(null);

  // ControlLabel "ears" resolution. Precedence: own explicit props >
  // ControlLabelContext board > built-in defaults. Size vocabulary is
  // identical ('sm' | 'md'), so no mapping is needed.
  const resolvedSize = size ?? cl?.size ?? defaultSize;
  // The resolved disabled must drive every disabled site consistently:
  // the colors memo, the DS-Switch-disabled class, the input cursor and
  // the StyledInput disabled attribute.
  const resolvedDisabled = disabled ?? cl?.disabled;
  // `error` from ControlLabelContext is intentionally IGNORED on the
  // control: Switch has no error styling — error is message-only by design.

  const isUncontrolled = checked === undefined;
  const wasUncontrolled = useRef(checked === undefined);
  if (isUncontrolled !== wasUncontrolled.current) {
    console.error(
      'Cannot change Switch from controlled to uncontrolled or vice-versa'
    );
  }
  const [innerChecked, setInnerChecked] = useState(!!checked);
  const isChecked = isUncontrolled ? innerChecked : checked;

  const colors = useMemo(() => {
    if (isChecked) {
      if (resolvedDisabled) {
        return {
          normal: tokens.theme.disabled.bg,
          hover: tokens.theme.disabled.bg,
          active: tokens.theme.disabled.bg,
          thumb: tokens.theme.disabled.surface,
        };
      } else {
        return {
          normal: tokens.theme.bg.selection.primary,
          hover: tokens.theme.bg.selection.primaryHover,
          active: tokens.theme.bg.selection.primaryHover,
          thumb: tokens.theme.bg.selection.surface,
        };
      }
    } else {
      if (resolvedDisabled) {
        return {
          normal: tokens.theme.disabled.bg,
          hover: tokens.theme.disabled.bg,
          active: tokens.theme.disabled.bg,
          thumb: tokens.theme.disabled.surface,
        };
      } else {
        return {
          normal: tokens.theme.bg.structural.surfaceStrong,
          hover: tokens.theme.bg.action.surfaceStrongHover,
          active: tokens.theme.bg.action.surfaceStrongActive,
          thumb: tokens.theme.bg.selection.surface,
        };
      }
    }
  }, [resolvedDisabled, isChecked]);

  useEffect(() => {
    const el = thumbRef.current;
    const transitionStart = ({ propertyName }: TransitionEvent) => {
      const classList = thumbRef.current?.classList;
      if (propertyName === 'left' && classList) {
        classList.remove('was-checked');
        classList.remove('was-unchecked');
      }
    };
    el?.addEventListener('transitionstart', transitionStart);
    return () => {
      el?.removeEventListener('transitionstart', transitionStart);
    };
  }, [thumbRef]);

  return (
    <Root
      sx={{
        fontSize: styles[resolvedSize].fontSize,
        backgroundColor: colors.normal,
        '&:hover': {
          backgroundColor: colors.hover,
        },
        '&:active': {
          backgroundColor: colors.active,
        },
        ...sx,
      }}
      data-testid={testId}
      role="switch"
      {...rest}
      className={clsx(
        isChecked && 'DS-Switch-checked',
        resolvedDisabled && 'DS-Switch-disabled',
        rest.className
      )}
      ref={ref}
    >
      <StyledInput
        name={name}
        checked={checked}
        // Board (ControlLabelContext) required surfaces as aria-required on
        // the native input.
        aria-required={cl?.required === true ? true : undefined}
        onChange={(event) => {
          onChange?.(event, !isChecked);
          thumbRef.current?.classList.add(
            `was-${isChecked ? 'unchecked' : 'checked'}`
          );
          setInnerChecked(!isChecked);
        }}
        type="checkbox"
        sx={{
          cursor: resolvedDisabled ? 'initial' : 'pointer',
        }}
        disabled={resolvedDisabled}
        // The label's `for` points at this id; the control's own inputId wins.
        id={inputId ?? cl?.id}
      />
      <StyledThumb
        sx={{
          backgroundColor: colors.thumb,
          '.DS-Switch-Root:active &': styles[resolvedSize].activeCss,
        }}
        ref={thumbRef}
      />
    </Root>
  );
};

Switch.displayName = 'Switch';
